// =====================================================================
// LLAVES DE ACCESO EN EL NAVEGADOR · ESTABILIDAD Parte D · D3
// =====================================================================
// El servidor manda las opciones de la ceremonia como JSON (los bytes en
// base64url) y el navegador las quiere como ArrayBuffer. Aquí se hace la
// traducción de ida y vuelta, y nada más: la huella o el Face ID los pide
// el propio sistema del aparato, y la parte privada de la llave nunca
// sale de él.
import { api, ErrorApi } from '../api';

export function aBytes(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
    + '==='.slice((b64url.length + 3) % 4);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function aTexto(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** ¿Este navegador sabe usar llaves de acceso? */
export function hayLlavesEnEsteAparato() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!navigator?.credentials?.create;
}

function opcionesDeRegistro(o) {
  return {
    ...o,
    challenge: aBytes(o.challenge),
    user: { ...o.user, id: aBytes(o.user.id) },
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: aBytes(c.id) })),
  };
}

function opcionesDeEntrada(o) {
  return {
    ...o,
    challenge: aBytes(o.challenge),
    allowCredentials: (o.allowCredentials || []).map((c) => ({ ...c, id: aBytes(c.id) })),
  };
}

function credencialAJson(c) {
  const r = c.response;
  const salida = {
    id: c.id,
    rawId: aTexto(c.rawId),
    type: c.type,
    clientExtensionResults: c.getClientExtensionResults?.() || {},
    response: { clientDataJSON: aTexto(r.clientDataJSON) },
  };
  // L-3: el tipo de llave (del aparato o física). El servidor lo guarda
  // para decir en la lista «de este aparato» o «llave física»; sin él
  // se quedaba vacío siempre.
  if (c.authenticatorAttachment) salida.authenticatorAttachment = c.authenticatorAttachment;
  if (r.attestationObject) {
    salida.response.attestationObject = aTexto(r.attestationObject);
    salida.transports = r.getTransports?.() || [];
  }
  if (r.authenticatorData) {
    salida.response.authenticatorData = aTexto(r.authenticatorData);
    salida.response.signature = aTexto(r.signature);
    if (r.userHandle) salida.response.userHandle = aTexto(r.userHandle);
  }
  return salida;
}

/** Agrega una llave de acceso a la cuenta de quien ya entró. */
// Desde L-3 pide el paso seguro (`paso`); sin él el servidor contesta 403.
export async function agregarLlave(apodo, paso) {
  const opciones = await pedirOpcionesDeRegistro(paso);
  return crearLlave(opciones, apodo, paso);
}

// =====================================================================
// ENTRAR SIN ESCRIBIR EL CORREO · DIRECTRIZ-LLAVE L-2 (6 oct 2026)
// =====================================================================
// El servidor (L-1) ya no necesita saber quién entra antes de la firma:
// `opciones` contesta siempre lo mismo, con `allowCredentials` vacío y la
// verificación de usuario obligatoria, y quien entra lo dice la llave por
// su `userHandle`. Así que aquí el correo es una PISTA: si está escrito
// viaja, y si no, no pasa nada.
//
// LAS DOS CEREMONIAS. La del botón («modal») abre la ventana del sistema
// con las llaves que tenga el aparato, o el QR del teléfono. La del
// relleno automático («conditional») no abre nada: deja las llaves en el
// menú que el navegador pinta encima del campo de correo, y solo termina
// si la persona elige una. Las dos van por esta misma función: una sola
// traducción de bytes, una sola forma de llamar al servidor.
//
// `fase` VIAJA EN EL ERROR. La pantalla tiene que distinguir «el aparato
// no dio ninguna llave» (gris: no es un problema, es que aquí no hay) de
// «el servidor rechazó la firma» (rojo). Un `NotAllowedError` solo
// significa lo primero si lo lanzó el aparato.

/** ¿Este navegador sabe pedir una llave para entrar? */
export function navegadorAdmiteLlaves() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!globalThis.navigator?.credentials?.get;
}

/** ¿El navegador ofrece las llaves en el menú del campo de correo? */
export async function rellenoAutomaticoDisponible() {
  try {
    if (!navegadorAdmiteLlaves()) return false;
    const preguntar = window.PublicKeyCredential.isConditionalMediationAvailable;
    if (typeof preguntar !== 'function') return false;
    return (await preguntar.call(window.PublicKeyCredential)) === true;
  } catch {
    return false;
  }
}

/** El aparato cerró la ventana sin dar ninguna llave (no hay, o se canceló). */
export function aparatoSinLlave(err) {
  return err?.fase === 'aparato'
    && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

function conFase(err, fase) {
  try { err.fase = fase; } catch { /* un error congelado se queda como está */ }
  return err;
}

function cortado() {
  const e = new Error('La ceremonia se cortó antes de pedir la llave.');
  e.name = 'AbortError';
  return conFase(e, 'aparato');
}

// EL RELLENO AUTOMÁTICO SE RENUEVA ANTES DE QUE CADUQUE SU RETO. El
// servidor da 2 minutos a cada reto (L-1); quien abre esta pantalla, lee y
// elige la llave del menú al tercer minuto firmaría un reto vencido. Se
// pide otro a los 100 segundos, sin que nadie lo note.
export const RELEVO_CONDICIONAL_MS = 100 * 1000;

// LO QUE SE DICE CUANDO LA LLAVE NO ENTRA. El 401 y el 429 del servidor ya
// dicen qué pasó y qué hacer (un solo texto para todo rechazo, a propósito:
// no delata si la llave existe). Lo demás —red caída, servidor sin
// contestar, un aparato que se niega por otra razón— recibe este.
const NO_ENTRO_CON_LLAVE = 'No se pudo entrar con la llave. Intenta de nuevo '
  + 'o entra con tu contraseña y tu código.';

// UNA LLAVE QUE TEGO YA NO TIENE · DIRECTRIZ-LLAVE L-4 (R-LL-11). El
// aparato la sigue ofreciendo después de quitarla en Mi cuenta. El servidor
// lo dice con su código, y la pantalla lo pinta en GRIS con el camino a
// Llaves de acceso, no en rojo: nadie hizo nada mal.
export const LLAVE_NO_REGISTRADA = 'LLAVE_NO_REGISTRADA';

export function llaveNoRegistrada(err) {
  return err?.fase === 'entrar' && err.status === 401 && err.codigo === LLAVE_NO_REGISTRADA;
}

// Y se le pide al aparato que la olvide, para que no la vuelva a ofrecer.
// Es una petición: el navegador que no la conoce (o la ignora) sigue igual.
async function pedirQueLaOlvide(rpId, credentialId) {
  try {
    const olvidar = window.PublicKeyCredential?.signalUnknownCredential;
    if (typeof olvidar !== 'function' || !rpId || !credentialId) return;
    await olvidar.call(window.PublicKeyCredential, { rpId, credentialId });
  } catch { /* el aparato decide */ }
}

export function textoDeFalloDeLlave(err) {
  if (err?.fase === 'entrar' && (err.status === 401 || err.status === 429) && err.message) {
    return err.message;
  }
  if (err?.fase === 'opciones' && err.status === 429 && err.message) return err.message;
  return NO_ENTRO_CON_LLAVE;
}

/**
 * Entra con una llave de acceso. Devuelve la misma respuesta que el login.
 *
 * `pista` (opcional): el correo, si ya está escrito. No es condición.
 * `mediation` y `signal` (opcionales): para el relleno automático.
 */
export async function entrarConLlave(pista, { mediation, signal } = {}) {
  const correo = typeof pista === 'string' ? pista.trim() : '';
  const conPista = correo ? { email: correo } : {};
  let opciones;
  try {
    opciones = await api.post('/auth/llave/opciones', conPista);
  } catch (err) {
    throw conFase(err, 'opciones');
  }
  if (signal?.aborted) throw cortado();
  const pedido = { publicKey: opcionesDeEntrada(opciones) };
  if (mediation) pedido.mediation = mediation;
  if (signal) pedido.signal = signal;
  let credencial;
  try {
    credencial = await globalThis.navigator.credentials.get(pedido);
  } catch (err) {
    throw conFase(err, 'aparato');
  }
  if (!credencial) throw cortado();
  try {
    return await api.post('/auth/llave/entrar', { ...conPista, respuesta: credencialAJson(credencial) });
  } catch (err) {
    conFase(err, 'entrar');
    if (llaveNoRegistrada(err)) await pedirQueLaOlvide(opciones.rpId, credencial.id);
    throw err;
  }
}

// =====================================================================
// EL ENLACE A «LLAVES DE ACCESO» · contrato entre L-2 y L-3
// =====================================================================
// La pantalla de entrada manda a quien no tiene llave a Mi cuenta, a la
// sección de llaves, y el enlace tiene que sobrevivir al inicio de sesión.
//
// POR QUÉ UN PARÁMETRO Y NO `#llaves`. `?m=` sobrevive porque el módulo de
// navegación siembra la pila con la dirección ENTERA (`urlActual()`, en
// `ui/navegacion.js`), y un parámetro de más va con ella. Una almohadilla
// también viajaría, pero el navegador solo salta a ella al cargar la
// página, cuando Mi cuenta todavía no existe: el salto lo tiene que hacer
// la pantalla igual, y entonces es más claro leerlo de la consulta, que es
// donde TEGO guarda todo lo demás.
//
// EL CONTRATO: `/app?m=mi-cuenta&seccion=llaves`. Mi cuenta (L-3) lee
// `seccionPedida()` y, si da `llaves`, lleva la vista y el foco al bloque
// con `id="llaves-de-acceso"`.
export const SECCION_LLAVES = 'llaves';
export const ANCLA_LLAVES = 'llaves-de-acceso';
export const ENLACE_LLAVES = `/app?m=mi-cuenta&seccion=${SECCION_LLAVES}`;

/** La sección de Mi cuenta que pide la dirección, o null. */
export function seccionPedida(busqueda) {
  try {
    const q = new URLSearchParams(busqueda ?? window.location.search);
    if (q.get('m') !== 'mi-cuenta') return null;
    return q.get('seccion') || null;
  } catch {
    return null;
  }
}

// =====================================================================
// MI CUENTA → SEGURIDAD → LLAVES DE ACCESO · DIRECTRIZ-LLAVE L-3
// =====================================================================
// EL PASO SEGURO. Agregar o quitar una llave cambia quién puede entrar,
// así que el servidor (L-1) exige haber confirmado la contraseña (y el
// código, si la persona lo tiene) hace menos de 5 minutos. El
// comprobante vive AQUÍ, en la memoria de la pestaña, y en ningún otro
// sitio: ni `localStorage` ni `sessionStorage`, que sobreviven a la
// pestaña y los lee cualquier guion de la página. Se da por vencido 15
// segundos antes que en el servidor para no llegar tarde por un pelo.
const MARGEN_PASO_MS = 15 * 1000;
let pasoSeguro = null;

/** El comprobante vigente, o null si hay que volver a confirmar. */
export function pasoSeguroVigente(ahora = Date.now()) {
  if (!pasoSeguro || pasoSeguro.vence - MARGEN_PASO_MS <= ahora) return null;
  return pasoSeguro.token;
}

export function olvidarPasoSeguro() {
  pasoSeguro = null;
}

const SIN_CONEXION = 'No hubo respuesta del servidor porque la conexión '
  + 'se cortó. Revisa tu internet y vuelve a intentarlo.';

// UNA PETICIÓN CON EL PASO SEGURO EN LA CABECERA. `api.del` no deja poner
// cabeceras y la baja solo lo lee de ahí (L-1). Y la confirmación de
// identidad NO puede pasar por `api`: su 401 es «contraseña equivocada»,
// y `api` lo tomaría por sesión caducada y sacaría a la persona a la
// puerta con lo escrito a medias.
async function pedirConPaso(metodo, ruta, datos, paso, { esLaConfirmacion = false } = {}) {
  const cabeceras = { 'Content-Type': 'application/json' };
  if (paso) cabeceras['X-Paso-Seguro'] = paso;
  let r;
  try {
    r = await globalThis.fetch(`/api${ruta}`, {
      method: metodo, headers: cabeceras,
      body: datos === undefined ? undefined : JSON.stringify(datos),
    });
  } catch {
    throw new ErrorApi(SIN_CONEXION, 0);
  }
  let cuerpo = null;
  try { cuerpo = await r.json(); } catch { cuerpo = null; }
  if (r.ok) return cuerpo;
  const detalle = (cuerpo && typeof cuerpo.detalle === 'string' && cuerpo.detalle)
    || (cuerpo && typeof cuerpo.detail === 'string' && cuerpo.detail)
    || (r.status === 429
      ? 'Lo intentaste demasiadas veces seguidas. Espera unos minutos y vuelve a intentarlo.'
      : 'El servidor no completó lo que pediste. Vuelve a intentarlo en unos segundos.');
  const e = new ErrorApi(detalle, r.status);
  if (r.status === 403 && !esLaConfirmacion) {
    // El único 403 de estas rutas es el paso seguro vencido o ausente.
    olvidarPasoSeguro();
    e.codigo = 'PASO_SEGURO';
  }
  if (r.status === 401 && !esLaConfirmacion) {
    // Aquí sí es la sesión: se deja que `api` lo note y la cierre como
    // en todas las demás pantallas.
    api.get('/auth/yo').catch(() => {});
  }
  throw e;
}

/** Confirma la identidad (contraseña y, si lo tiene, código) por 5 minutos. */
export async function confirmarIdentidad(password, codigo = '') {
  const r = await pedirConPaso('POST', '/seguridad/llave/reautenticar',
    { password, codigo: (codigo || '').replace(/\s+/g, '') }, null, { esLaConfirmacion: true });
  const segundos = Number(r?.vence_en_segundos) || 300;
  pasoSeguro = { token: r.paso_seguro, vence: Date.now() + segundos * 1000 };
  return r;
}

/** Paso 1 del alta: el reto y el nombre sugerido. Pide el paso seguro. */
export async function pedirOpcionesDeRegistro(paso) {
  const opciones = await pedirConPaso('POST', '/seguridad/llave/registro/opciones',
    { paso_seguro: paso }, paso);
  return { ...opciones, recibidas_en: Date.now() };
}

/**
 * Paso 2 del alta: el aparato crea la llave y el servidor la guarda.
 * Lanza el error del aparato con `fase = 'aparato'` (cancelar no es un
 * fallo: la pantalla lo pinta en gris).
 */
export async function crearLlave(opciones, apodo, paso) {
  const { nombre_sugerido: _n, recibidas_en: _r, ...publicKey } = opciones;
  let credencial;
  try {
    credencial = await globalThis.navigator.credentials.create({ publicKey: opcionesDeRegistro(publicKey) });
  } catch (err) {
    throw conFase(err, 'aparato');
  }
  if (!credencial) throw cortado();
  return pedirConPaso('POST', '/seguridad/llave/registro/confirmar', {
    respuesta: credencialAJson(credencial), apodo: (apodo || '').trim(), paso_seguro: paso,
  }, paso);
}

/** Quita una de mis llaves. Pide el paso seguro (cabecera). */
export function quitarMiLlave(id, paso) {
  return pedirConPaso('DELETE', `/seguridad/llaves/${id}`, undefined, paso);
}

// LA SECCIÓN PEDIDA DESDE DENTRO DE LA APLICACIÓN. La tarjeta «Entra con
// un toque» (R-LL-4) ya está dentro: navega con el módulo de navegación,
// que escribe en la dirección solo las claves de su tabla, y `seccion` no
// está en ella. Así que lo deja apuntado aquí, en memoria, y Mi cuenta lo
// toma una sola vez al montarse. Si Mi cuenta ya está abierta, el aviso
// `tego:seccion-de-mi-cuenta` la lleva sin volver a montarla.
let seccionApuntada = null;
export const AVISO_SECCION = 'tego:seccion-de-mi-cuenta';

export function apuntarSeccion(seccion) {
  seccionApuntada = seccion;
  try { window.dispatchEvent(new CustomEvent(AVISO_SECCION, { detail: { seccion } })); } catch { /* sin ventana */ }
}

/** La sección pedida por la dirección o por la tarjeta; se consume al leerla. */
export function tomarSeccionPedida() {
  const deLaDireccion = seccionPedida();
  const apuntada = seccionApuntada;
  seccionApuntada = null;
  return deLaDireccion || apuntada;
}

// «AHORA NO» ES PARA SIEMPRE, Y ES DE CADA PERSONA (R-LL-4). En este
// navegador: el servidor no tiene todavía un lugar para preferencias
// sueltas de la persona (`/cuenta/mi/preferencias` solo guarda aspecto e
// idioma). Si el almacén no está (modo privado), la tarjeta se cierra
// por ahora y nada se rompe.
const CLAVE_NO_OFRECER = 'tego_llave_no_ofrecer';

export function llaveYaRechazada(idUsuario) {
  try { return localStorage.getItem(`${CLAVE_NO_OFRECER}:${idUsuario}`) === '1'; } catch { return false; }
}

export function rechazarOfertaDeLlave(idUsuario) {
  try { localStorage.setItem(`${CLAVE_NO_OFRECER}:${idUsuario}`, '1'); } catch { /* sin almacén */ }
}
